import { api } from './api.ts'
import { toApiError } from './api-error.ts'
import { isTvName, type DisplayPage } from './display-mode.ts'
import { parseTheme, type Theme } from './theme.ts'
import { YOUTUBE_VIDEO_ID } from './youtube-video.ts'

/**
 * What a playlist load came to.
 *
 * `missing` is a display that is not set up (unknown name, empty list, or a
 * list that names a different TV). `offline` is anything the screen should
 * retry: the network, a malformed body, or a refusal other than 400 and 404.
 */
export type PlaylistLoad =
  | { status: 'ready'; name: string; pages: DisplayPage[]; theme: Theme }
  | { status: 'missing' }
  | { status: 'offline' }

/**
 * Whether a playlist path is a page this app can show.
 *
 * A path that is itself a display would rotate into the rotator. A
 * protocol-relative path would leave the app. A backslash is not a path
 * this router serves. All of those are dropped.
 *
 * @param path - The path the server sent, query string included.
 * @returns True when the router can show it.
 */
function isPlayable(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
    return false
  }
  const pathname = path.split(/[?#]/)[0] ?? path

  return pathname !== '/display' && !pathname.startsWith('/display/')
}

/** A Studio document id, as the playlist item carries it. */
const DASHBOARD_ID = /^[A-Za-z0-9_-]{1,128}$/

/** The console path a dashboard item plays as, before `?id=`. */
const TV_DASHBOARD_PATH = '/studio/tv'

type Kept = { path: string; seconds: number }

function dwellOk(seconds: number): boolean {
  return Number.isSafeInteger(seconds) && seconds >= 10 && seconds <= 3600
}

/**
 * Keep a path the router can show, inside the dwell bounds.
 *
 * @param path - The path to open.
 * @param seconds - The dwell, already known to be a number.
 * @returns The page, or null when this one is dropped.
 */
function playablePage(path: string, seconds: number): Kept | null {
  if (!isPlayable(path) || !dwellOk(seconds)) return null

  return { path, seconds }
}

/**
 * One page from the old `pages` list.
 *
 * A page that is not the contract makes the whole body `offline`. A page
 * outside the path or dwell rule is dropped.
 *
 * @param page - One entry of `pages`.
 * @returns The page, `offline` when the entry is not the contract, or null when it is dropped.
 */
function readPage(page: unknown): Kept | 'offline' | null {
  if (
    typeof page !== 'object' ||
    page === null ||
    !('path' in page) ||
    !('seconds' in page)
  ) {
    return 'offline'
  }
  const { path, seconds } = page
  if (typeof path !== 'string' || typeof seconds !== 'number') return 'offline'

  return playablePage(path, seconds)
}

/**
 * One entry from `items`.
 *
 * A page becomes `{path, seconds}`. A dashboard whose id matches the Studio
 * id shape becomes `/studio/tv?id=<dashboardId>`. A video becomes
 * `/display-video?id=<videoId>` after its id passes validation. An unknown type is dropped,
 * so a later backend can add a type without making this body `offline`.
 *
 * @param item - One entry of `items`.
 * @returns The page, `offline` when a known type is not the contract, or null when it is dropped.
 */
function readItem(item: unknown): Kept | 'offline' | null {
  if (typeof item !== 'object' || item === null) return 'offline'
  // No type is a page, as the backend reads a stored entry.
  const type = 'type' in item ? item.type : 'page'
  if (type !== 'page' && type !== 'dashboard' && type !== 'video') return null
  if (type === 'video') {
    if (!('videoId' in item) || !('seconds' in item)) return 'offline'
    const { videoId, seconds } = item
    if (typeof videoId !== 'string' || typeof seconds !== 'number')
      return 'offline'
    if (!YOUTUBE_VIDEO_ID.test(videoId)) return null
    const sound = 'sound' in item && item.sound === true ? '&sound=1' : ''
    return playablePage(`/display-video?id=${videoId}${sound}`, seconds)
  }
  if (type === 'dashboard') {
    if (!('dashboardId' in item) || !('seconds' in item)) return 'offline'
    const { dashboardId, seconds } = item
    if (typeof dashboardId !== 'string' || typeof seconds !== 'number') {
      return 'offline'
    }
    if (!DASHBOARD_ID.test(dashboardId)) return null

    return playablePage(`${TV_DASHBOARD_PATH}?id=${dashboardId}`, seconds)
  }
  if (!('path' in item) || !('seconds' in item)) return 'offline'
  const { path, seconds } = item
  if (typeof path !== 'string' || typeof seconds !== 'number') return 'offline'

  return playablePage(path, seconds)
}

/**
 * The theme a playlist body paints.
 *
 * Absent and unknown are dark. A bad theme does not make the body offline:
 * the pages are still the contract, and the TV paints dark.
 *
 * @param body - A playlist body that already matched the contract.
 * @returns The theme to paint.
 */
function readTheme(body: object): Theme {
  return ('theme' in body ? parseTheme(body.theme) : null) ?? 'dark'
}

/** A collected playlist, before its theme is read. A bad theme must not change this. */
type Collected =
  | { status: 'ready'; name: string; pages: DisplayPage[] }
  | { status: 'missing' }
  | { status: 'offline' }

/**
 * Walk one list into the pages the screen rotates.
 *
 * @param name - The TV name, already checked against the route.
 * @param entries - `items` when the body has it, otherwise `pages`.
 * @param read - How one entry is read.
 * @returns The pages, or why there are none. Theme is applied by the caller.
 */
function collect(
  name: string,
  entries: unknown[],
  read: (entry: unknown) => Kept | 'offline' | null
): Collected {
  const playable: Kept[] = []
  for (const entry of entries) {
    const result = read(entry)
    if (result === 'offline') return { status: 'offline' }
    if (result !== null) playable.push(result)
  }
  if (playable.length === 0) return { status: 'missing' }

  return { status: 'ready', name, pages: playable }
}

/**
 * Read a playlist body.
 *
 * A body that carries `items` is read from `items`. Otherwise `pages` is
 * read as before. A body that is not the contract is `offline`, so the
 * screen retries it. A body that matches the contract but names no playable
 * page is `missing`. A page needs an integer dwell from 10 through 3600
 * seconds. Drop anything outside the backend's bounds. An item of unknown
 * type is dropped, not `offline`. `theme` is `dark` or `light`. Absent and
 * unknown are `dark`, and neither makes the body `offline`.
 *
 * @param body - The parsed JSON.
 * @param requested - The TV the route asked for.
 * @returns The load result. Never throws.
 */
function interpret(body: unknown, requested: string): PlaylistLoad {
  if (typeof body !== 'object' || body === null || !('name' in body)) {
    return { status: 'offline' }
  }
  const name = body.name
  if (typeof name !== 'string') return { status: 'offline' }
  const useItems = 'items' in body
  const entries = useItems
    ? body.items
    : 'pages' in body
      ? body.pages
      : undefined
  if (!Array.isArray(entries)) return { status: 'offline' }
  if (name !== requested) return { status: 'missing' }

  const loaded = collect(name, entries, useItems ? readItem : readPage)
  if (loaded.status !== 'ready') return loaded

  return { ...loaded, theme: readTheme(body) }
}

/**
 * Load one TV's playlist.
 *
 * A name the contract rejects is not requested. The browser reporting
 * offline is not requested either: the notice retries, and a request that
 * cannot leave the machine would only delay that.
 *
 * 400 and 404 are "not set up". Every other failure, including a 403, is
 * offline and retries. The server never returns a PII page; this client
 * does not second-guess that.
 *
 * @param name - The TV name from the route.
 * @returns The load result.
 */
export async function loadDisplayPlaylist(name: string): Promise<PlaylistLoad> {
  if (!isTvName(name)) {
    return { status: 'missing' }
  }
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { status: 'offline' }
  }
  try {
    const body: unknown = await api
      .get('display-playlist', { searchParams: { name } })
      .json()

    return interpret(body, name)
  } catch (error) {
    const apiError = await toApiError(error)
    if (apiError.status === 400 || apiError.status === 404) {
      return { status: 'missing' }
    }

    return { status: 'offline' }
  }
}
